import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Star, BellSimple } from "@phosphor-icons/react";
import { AppHeader } from "../components/AppHeader";
import { PriceChart } from "../components/PriceChart";
import { ChangeIndicator } from "../components/ChangeIndicator";
import { DataStatus } from "../components/DataStatus";
import { CurrencyConverter } from "../components/CurrencyConverter";
import { AlertFormDialog } from "../components/AlertFormDialog";
import { Disclaimer } from "../components/Disclaimer";
import { Skeleton } from "../components/ui/skeleton";
import { useFavorites } from "../hooks/useFavorites";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { fmtNum } from "../lib/format";

const RANGES = ["7D", "30D", "3M", "6M", "1Y"];

export default function CurrencyDetail() {
  const { code } = useParams();
  const nav = useNavigate();
  const [range, setRange] = useState("30D");
  const { isFav, toggle } = useFavorites();
  const { user } = useAuth();
  const base = user?.base_currency || "USD";

  const detail = useQuery({
    queryKey: ["currency", code, base],
    queryFn: async () => (await api.get(`/currencies/${code}`, { params: { base } })).data,
  });
  const history = useQuery({
    queryKey: ["currency-hist", code, range, base],
    queryFn: async () => (await api.get(`/currencies/${code}/history`, { params: { range, base } })).data,
    enabled: !!detail.data?.hist_supported,
  });

  const d = detail.data;
  const fav = d ? isFav("currency", d.code) : false;
  const favPayload = d ? { asset_type: "currency", symbol: d.code, name: d.name } : null;

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
        <button onClick={() => nav(-1)} data-testid="back-btn" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft size={16} /> Back
        </button>

        {detail.isLoading ? <Skeleton className="h-32 rounded-none" /> : d && (
          <>
            <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="font-heading text-4xl font-black tracking-tight" data-testid="detail-code">{d.code}</h1>
                  <span className="text-xs uppercase tracking-widest text-muted-foreground border border-border px-2 py-1">{d.country}</span>
                </div>
                <p className="text-muted-foreground mt-1">{d.name}</p>
                <div className="mt-4 flex items-baseline gap-3">
                  <span className="text-sm text-muted-foreground">1 {base} =</span>
                  <span className="font-mono text-3xl font-bold tracking-tight" data-testid="detail-rate">{fmtNum(d.rate)}</span>
                  <span className="text-lg text-muted-foreground">{d.code}</span>
                </div>
                <div className="mt-2 flex items-center gap-3">
                  <ChangeIndicator value={d.change} size="lg" />
                  <DataStatus updatedAt={d.updated_at} stale={d.stale} testid="currency-detail-status" />
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => toggle(favPayload)} data-testid="detail-fav" className="inline-flex items-center gap-2 border border-border bg-card px-4 h-10 hover-lift text-sm">
                  <Star size={16} weight={fav ? "fill" : "regular"} className={fav ? "text-primary" : ""} /> {fav ? "Watching" : "Watch"}
                </button>
                <AlertFormDialog asset={{ asset_type: "currency", symbol: d.code, name: d.name }} base={base}
                  trigger={<button data-testid="detail-alert" className="inline-flex items-center gap-2 border border-border bg-card px-4 h-10 hover-lift text-sm"><BellSimple size={16} /> Alert</button>} />
              </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 border border-border bg-card p-5">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground">Historical Rate</h3>
                  {d.hist_supported && (
                    <div className="inline-flex border border-border" data-testid="range-tabs">
                      {RANGES.map((r) => (
                        <button key={r} onClick={() => setRange(r)} data-testid={`range-${r}`}
                          className={`px-3 py-1.5 text-xs font-mono ${range === r ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>{r}</button>
                      ))}
                    </div>
                  )}
                </div>
                {d.hist_supported ? (
                  history.isLoading ? <Skeleton className="h-[300px] rounded-none" /> :
                    <PriceChart points={history.data?.points} />
                ) : (
                  <div className="flex items-center justify-center border border-dashed border-border h-[300px] text-sm text-muted-foreground text-center px-6" data-testid="hist-unavailable">
                    Historical data is not available for {d.code} from our data provider.
                  </div>
                )}
              </div>
              <CurrencyConverter defaultTo={d.code} />
            </div>
          </>
        )}
        <Disclaimer />
      </main>
    </div>
  );
}
